import type { AppDispatch } from '@/app/store';
import { queryClient } from '@/app/providers/queryClient';
import { logout } from '@/features/auth/store/authSlice';
import type {
    User,
    PermissionCheck,
    ResourceType,
    ActionType,
    Permission,
} from '../types/auth.types';

export const handleLogout = (dispatch: AppDispatch) => {
    dispatch(logout());
    queryClient.clear();
};

export const checkPermission = (user: User, check: PermissionCheck): boolean => {
    if (!user) return false;

    // Check direct permissions
    const hasDirectPermission =
        user.permissions?.some(
            (permission) =>
                permission.resource === check.resource && permission.action === check.action
        ) ?? false;

    if (hasDirectPermission) return true;

    // Check role-based permissions
    const hasRolePermission =
        user.roles?.some((role) =>
            role.permissions.some(
                (permission) =>
                    permission.resource === check.resource && permission.action === check.action
            )
        ) ?? false;

    return hasRolePermission;
};

export const checkRole = (user: User, roleName: string): boolean => {
    if (!user || !user.roles || user.roles.length === 0) return false;
    return user.roles.some((role) => role.name === roleName);
};

export const checkAnyRole = (user: User, roleNames: string[]): boolean => {
    if (!user || !user.roles || user.roles.length === 0) return false;
    return roleNames.some((roleName) => checkRole(user, roleName));
};

export const checkAllRoles = (user: User, roleNames: string[]): boolean => {
    if (!user || !user.roles || user.roles.length === 0) return false;
    return roleNames.every((roleName) => checkRole(user, roleName));
};

export const hasAccess = (
    user: User | null,
    access: { roles?: string[]; permissions?: PermissionCheck[] } = {}
): boolean => {
    if (!user) return false;

    const { roles, permissions } = access;

    // If no specific access is required, grant access.
    if ((!roles || roles.length === 0) && (!permissions || permissions.length === 0)) {
        return true;
    }

    // Check if the user has any of the required roles.
    if (roles && roles.length > 0 && checkAnyRole(user, roles)) {
        return true;
    }

    // Check if the user has any of the required permissions.
    if (
        permissions &&
        permissions.length > 0 &&
        permissions.some((permission) => checkPermission(user, permission))
    ) {
        return true;
    }

    return false;
};

export const filterByPermission = <T extends { id: string }>(
    items: T[],
    user: User,
    resource: ResourceType,
    action: ActionType
): T[] => {
    const canAccess = checkPermission(user, { resource, action });
    return canAccess ? items : [];
};

export const getUserRoleNames = (user: User): string[] => {
    if (!user || !user.roles || user.roles.length === 0) return [];
    return user.roles.map((role) => role.name);
};

export const getUserPermissions = (user: User): Permission[] => {
    // Get direct permissions
    const directPermissions = user.permissions || [];

    // Get permissions from roles
    const rolePermissions = user.roles?.flatMap((role) => role.permissions) || [];

    // Combine and deduplicate
    const allPermissions = [...directPermissions, ...rolePermissions];
    const uniquePermissions = allPermissions.filter(
        (permission, index, self) =>
            index ===
            self.findIndex(
                (p) => p.resource === permission.resource && p.action === permission.action
            )
    );

    return uniquePermissions;
};

export const isTokenExpired = (token: string): boolean => {
    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const currentTime = Date.now() / 1000;
        return payload.exp < currentTime;
    } catch {
        return true;
    }
};

export const getTokenExpiry = (token: string): Date | null => {
    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return new Date(payload.exp * 1000);
    } catch {
        return null;
    }
};
